import React from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import { ConstructionModeProvider, useConstructionMode } from "@/contexts/ConstructionModeContext";
import AdSenseScript from "@/components/AdSenseScript";
import { useUserRole } from "@/hooks/useUserRole";
import { useFeatureFlag } from "@/contexts/FeatureFlagsContext";
import { useAnalytics } from "@/hooks/useAnalytics";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import FAQ from "./pages/FAQ";

import Configuration from "./pages/Configuration";
import Analytics from "./pages/Analytics";
import RemoveBackground from "./pages/RemoveBackground";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import NotFound from "./pages/NotFound";
import Herramientas from "./pages/Herramientas";
import ToolPage from "./pages/ToolPage";
import UnderConstruction from "@/components/UnderConstruction";
import ConstructionModeToggle from "@/components/ConstructionModeToggle";

const queryClient = new QueryClient();

const ScrollToTop = () => {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const PageViewTracker = () => {
  const location = useLocation();
  const { trackPageView } = useAnalytics();
  
  React.useEffect(() => {
    if (location.pathname === '/analytics') return;
    trackPageView(location.pathname);
  }, [location.pathname, trackPageView]);
  
  return null;
};

const AppContent = () => {
  const { isConstructionMode } = useConstructionMode();
  const { isAdmin } = useUserRole();
  const backgroundRemovalEnabled = useFeatureFlag('background_removal');

  // Hide Lovable badge for non-admin users
  React.useEffect(() => {
    // Remove any existing style
    const existingStyle = document.getElementById('lovable-badge-style');
    if (existingStyle) {
      existingStyle.remove();
    }

    // Add new style based on current admin status
    const style = document.createElement('style');
    style.id = 'lovable-badge-style';
    style.textContent = `
      ${!isAdmin ? `
        [data-lovable-badge],
        .lovable-badge,
        iframe[src*="lovable"],
        div[style*="lovable"] {
          display: none !important;
          visibility: hidden !important;
        }
      ` : ''}
    `;
    document.head.appendChild(style);
    
    return () => {
      const styleElement = document.getElementById('lovable-badge-style');
      if (styleElement) {
        styleElement.remove();
      }
    };
  }, [isAdmin]);

  if (isConstructionMode) {
    return (
      <>
        <UnderConstruction />
        <ConstructionModeToggle />
      </>
    );
  }

  return (
    <>
      <BrowserRouter>
        <ScrollToTop />
        <PageViewTracker />
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/faq" element={<FAQ />} />
          {backgroundRemovalEnabled && (
            <Route path="/quitar-fondo" element={<RemoveBackground />} />
          )}
          <Route path="/configuration" element={<Configuration />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/privacidad" element={<Privacy />} />
          <Route path="/terminos" element={<Terms />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/herramientas" element={<Herramientas />} />
          <Route path="/herramientas/:slug" element={<ToolPage />} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
      <ConstructionModeToggle />
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AdSenseScript />
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <ConstructionModeProvider>
        <SubscriptionProvider>
          <AppContent />
        </SubscriptionProvider>
      </ConstructionModeProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
